'use client'
import styles from './TestimonialsBlock.module.scss'
import PageGutter from '@/src/components/common/PageGutter'
import useCarousel from '@/src/hooks/useCarousel'
import SVGChevron from '@/src/components/common/SVG/SVGChevron'
import SVGStar from '@/src/components/common/SVG/SVGStar'
import { PropsWithChildren, Ref, useImperativeHandle, useRef, useState } from 'react'
import PageSectionTitle from '@/src/components/common/PageSectionTitle'
import usePageLoad from '@/src/hooks/usePageLoad'
import {
  Review,
  TestimonialsBlockClientProps,
} from '@/src/components/blocks/FeatureBlock/TestimonialsBlock/TestimonialsBlock.types'
import { classNames } from '@/src/helpers/component'
import useWindowSize from '@/src/hooks/useWindowSize'
import SVGClose from '@/src/components/common/SVG/SVGClose'
import CTAButton from '@/src/components/common/CTAButton'

interface RemarkHandle {
  collapse: () => void
}

interface RemarkProps {
  ref?: Ref<RemarkHandle>
  review: Review
}

function Modal({ children, onClose }: PropsWithChildren<{ onClose: () => void }>) {
  return (
    <div
      className={classNames(styles.modal, 'modal-shown')}
      onClick={e => {
        if (!(e.target as HTMLElement).closest(`.${styles.modalContent}`)) onClose()
      }}>
      <div className={styles.modalContent}>
        <div className={styles.modalClose} onClick={() => onClose()}>
          <SVGClose height={12} />
        </div>
        <div className={styles.modalContentBody}>{children}</div>
      </div>
    </div>
  )
}

const REMARK_TRUNCATED_LENGTH = 370

function Remark({ ref, review }: RemarkProps) {
  const refRemark = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)
  const expandable = review.content.length > REMARK_TRUNCATED_LENGTH
  const [, setRemarkScrollHeight] = useState(0)

  useImperativeHandle(
    ref,
    () => ({
      collapse: () => setExpanded(false),
    }),
    [],
  )

  function initRemarkState() {
    const divRemark = refRemark.current
    if (!divRemark) return
    setRemarkScrollHeight(divRemark.scrollHeight)
  }

  usePageLoad(initRemarkState)
  useWindowSize(initRemarkState)

  return (
    <>
      <div className={classNames(styles.remarkWrapper, styles.collapsed)}>
        <h4 ref={refRemark} className={styles.remark}>
          {review.content.slice(0, REMARK_TRUNCATED_LENGTH).replace(/\s+[^\s]*$/g, '')}
          {expandable && (
            <>
              {'... '}
              <span className={styles.seeMore} onClick={() => setExpanded(true)}>
                See more
              </span>
            </>
          )}
        </h4>
      </div>
      {expanded && (
        <Modal onClose={() => setExpanded(false)}>
          <div>
            {new Array(review.stars).fill(null).map((_, idx) => (
              <SVGStar key={idx} width={20} height={20} />
            ))}
          </div>

          <div>{review.content}</div>

          <div style={{ fontWeight: 800 }}>{review.author}</div>
        </Modal>
      )}
    </>
  )
}

export default function TestimonialsBlockClient(_: TestimonialsBlockClientProps) {
  const {
    carousel,
    refCarousel,
    scrollToIdx,
    scrollToNextIdx,
    scrollToPrevIdx,
    calculatePositions,
    itemPositionIdx,
  } = useCarousel<HTMLDivElement>({
    circular: true,
    onScrollStart: isResizing => {
      if (isResizing) return
      carousel.current?.parentElement?.scrollIntoView({ behavior: 'smooth' })
      refRemarks.current.forEach(r => r?.collapse())
    },
  })

  const [itemsPositions, setItemsPositions] = useState<number[]>([])
  const refRemarks = useRef<(RemarkHandle | null)[]>([])

  usePageLoad(() => {
    setItemsPositions(calculatePositions())
  })

  if (!_.reviews.length) return null

  return (
    <div className={styles.container}>
      <PageGutter>
        <div className={styles.contentContainer}>
          <PageSectionTitle
            align={'left'}
            className={styles.headersAndViewAll}
            header={_.heading ?? ''}
            text={_.subheading ?? ''}
            cta={
              _.heading_cta_button ? { ..._.heading_cta_button, className: styles.viewAll } : null
            }
          />
          <div className={styles.remarksAndQuoteContainer}>
            <div ref={refCarousel} className={styles.remarksCarousel}>
              {_.reviews.map((testimonial, idx) => (
                <div key={idx} className={styles.remarksContainer}>
                  <div className={styles.starsContainer}>
                    {new Array(testimonial.stars).fill(null).map((_, idx) => (
                      <SVGStar key={idx} />
                    ))}
                  </div>
                  <Remark
                    ref={remarkHandler => {
                      refRemarks.current[idx] = remarkHandler
                    }}
                    review={testimonial}
                  />
                  <h4 className={styles.username}>{testimonial.author}</h4>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.carouselControllerContainer}>
            <div className={styles.btnCarouselController} onClick={scrollToPrevIdx}>
              <SVGChevron direction={'left'} />
            </div>
            {!!itemsPositions.length && (
              <div className={styles.dotsIndicatorContainer}>
                {itemsPositions.map((_, idx) => (
                  <span
                    onClick={() => scrollToIdx(idx)}
                    key={idx}
                    style={{
                      cursor: 'pointer',
                      display: 'inline-block',
                      width: 16,
                      height: 16,
                      borderRadius: 16,
                      background: idx === itemPositionIdx ? '#485DAA' : '#DFE1DF',
                    }}
                  />
                ))}
              </div>
            )}
            <div className={styles.btnCarouselController} onClick={scrollToNextIdx}>
              <SVGChevron direction={'right'} />
            </div>
          </div>

          {_.heading_cta_button && (
            <div className={styles.footerViewAll}>
              <CTAButton className={styles.viewAll} {..._.heading_cta_button} />
            </div>
          )}
        </div>
      </PageGutter>
    </div>
  )
}
